import React, { Suspense, lazy, useEffect, useRef } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import LoadingSpinner from './components/LoadingSpinner';
import ImpersonationBanner from './components/ImpersonationBanner';
import ErrorBoundary from './components/ErrorBoundary';
import { syncOverseer } from './services/overseerSync';

// PERF-001..006 — lazy-load every route. Public landing routes (Login,
// Checkout, Legal, Forgot/Reset Password) and the auth-gated routes that
// dominate page weight (admin pages, ProjectWizard with its editor, etc.)
// all become separate chunks so the initial bundle is much smaller.
// A failed dynamic import almost always means a new deploy replaced the chunk
// hashes our (now-stale) index.html references — a user who left the tab open
// across a deploy hits this the moment they navigate to a not-yet-loaded
// route. Force ONE full reload to pull the fresh index + chunks so they get
// the new build transparently (Suspense fallback shows during the reload)
// instead of the ErrorBoundary's "Something went wrong / page failed to load".
// A short-lived session timestamp prevents an infinite reload loop if the
// import keeps failing for a real reason (offline, server down).
function lazyWithRetry<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      return await factory();
    } catch (err) {
      const KEY = 'sf_chunk_reloaded_at';
      const last = Number(sessionStorage.getItem(KEY) || '0');
      if (Date.now() - last > 10000) {
        sessionStorage.setItem(KEY, String(Date.now()));
        window.location.reload();
        return new Promise<never>(() => {}); // hang until the reload takes over
      }
      throw err; // already reloaded recently → surface the real error
    }
  });
}

const LoginPage = lazyWithRetry(() => import('./pages/LoginPage'));
const CheckoutPage = lazyWithRetry(() => import('./pages/CheckoutPage'));
const Dashboard = lazyWithRetry(() => import('./pages/Dashboard'));
const ProjectWizard = lazyWithRetry(() => import('./pages/ProjectWizard'));
const ManagePage = lazyWithRetry(() => import('./pages/ManagePage'));
const PublishedPage = lazyWithRetry(() => import('./pages/PublishedPage'));
const CollabAcceptPage = lazyWithRetry(() => import('./pages/CollabAcceptPage'));
const PreviewPage = lazyWithRetry(() => import('./pages/PreviewPage'));
const SharePage = lazyWithRetry(() => import('./pages/SharePage'));
const PaymentLinkLanding = lazyWithRetry(() => import('./pages/PaymentLinkLanding'));
const OutreachClaimPage = lazyWithRetry(() => import('./pages/OutreachClaimPage'));
const AdminDashboard = lazyWithRetry(() => import('./pages/AdminDashboard'));
const AdminSettingsPage = lazyWithRetry(() => import('./pages/AdminSettings'));
const AdminClientsPage = lazyWithRetry(() => import('./pages/AdminClients'));
// #17 (2026-08-12) — staff/user management, promoted to its own top-level page.
const AdminUsersPage = lazyWithRetry(() => import('./pages/AdminUsers'));
const AdminPackagesPage = lazyWithRetry(() => import('./pages/AdminPackages'));
const AdminOrdersPage = lazyWithRetry(() => import('./pages/AdminOrders'));
const AdminPartnersPage = lazyWithRetry(() => import('./pages/AdminPartners'));
const AdminOutreachPage = lazyWithRetry(() => import('./pages/AdminOutreachDashboard'));
// #19 (2026-08-12) — AdminPromoCodes/AdminVpsAnalytics/AdminBedrockSpend/
// AdminPayouts/AdminEmailCampaigns/AdminKb no longer get their own routes;
// their *Content components are now rendered as tabs inside
// AdminPackages/AdminUsageReport/AdminPartners/AdminSettings respectively
// (see the redirect routes below), so their standalone lazy chunks aren't
// needed here anymore.
const EmailConfirmationPage = lazyWithRetry(() => import('./pages/EmailConfirmationPage'));
const ForgotPasswordPage = lazyWithRetry(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazyWithRetry(() => import('./pages/ResetPasswordPage'));
const PartnerSignup = lazyWithRetry(() => import('./pages/PartnerSignup'));
const PartnerDashboard = lazyWithRetry(() => import('./pages/PartnerDashboard'));
const PartnerSettings = lazyWithRetry(() => import('./pages/PartnerSettings'));
const PartnerClients = lazyWithRetry(() => import('./pages/PartnerClients'));
const PartnerOutreach = lazyWithRetry(() => import('./pages/PartnerOutreach'));
const AffiliateDashboard = lazyWithRetry(() => import('./pages/AffiliateDashboard'));
const AgencySignupPage = lazyWithRetry(() => import('./pages/AgencySignupPage'));
const ApiKeysPage = lazyWithRetry(() => import('./pages/ApiKeysPage'));
const BillingPage = lazyWithRetry(() => import('./pages/BillingPage'));
const BillingHistoryPage = lazyWithRetry(() => import('./pages/BillingHistoryPage'));
const ApiDocsPage = lazyWithRetry(() => import('./pages/ApiDocsPage'));
const SupportPage = lazyWithRetry(() => import('./pages/SupportPage'));
const AccountPage = lazyWithRetry(() => import('./pages/AccountPage'));
const AdminTicketsPage = lazyWithRetry(() => import('./pages/AdminTicketsPage'));
const AdminPermissions = lazyWithRetry(() => import('./pages/AdminPermissions'));
const AdminUsageReportPage = lazyWithRetry(() => import('./pages/AdminUsageReport'));
const ResumePage = lazyWithRetry(() => import('./pages/ResumePage'));
const LegalContentPage = lazyWithRetry(() => import('./pages/LegalContentPage'));
const SalesPortal = lazyWithRetry(() => import('./pages/SalesPortal'));

function HomeRoute({ children }: { children: React.ReactNode }) {
  const { user, isAdmin, isSubAdmin, isStaff, isSupport, loading } = useAuth();
  // #2178.4 — "/" is the only route that renders CheckoutPage inside this guard, and
  // CheckoutPage can sign a guest in mid-flow (Account step) or at Confirmation, once the
  // header needs to reflect it (see CheckoutPage's handleCheckout). That must never be
  // treated as "an already-signed-in customer landed on the marketing page" and bounced to
  // /dashboard — it would rip them out of their own checkout. Remember whether a session
  // already existed the FIRST time the auth check resolved; only THAT case redirects.
  // Becoming signed-in afterward, while still mounted here, can only be this page's own
  // checkout completing.
  const hadSessionOnLoad = useRef<boolean | null>(null);
  if (!loading && hadSessionOnLoad.current === null) {
    hadSessionOnLoad.current = !!user;
  }
  if (loading) return <LoadingSpinner fullPage />;
  if (hadSessionOnLoad.current === false) return <>{children}</>;
  // Bug #3 (17.08) — admin/superadmin AND sub_admin land on the admin console
  // (sub_admin gets the operational sections). Must precede the isStaff check,
  // since sub_admin is also isStaff.
  if (user && (isAdmin || isSubAdmin)) return <Navigate to="/admin" replace />;
  // #support-role — support staff land on the Tickets console, not the client
  // dashboard (they have no client_profiles row). Before the isStaff/dashboard fallbacks.
  if (user && isSupport) return <Navigate to="/admin/tickets" replace />;
  // Phase 2.2 — sales/va have no client_profiles row, so the generic
  // "/dashboard" fallback below (the client project dashboard) isn't meaningful
  // for them. Must come before that fallback.
  if (user && isStaff) return <Navigate to="/sales" replace />;
  if (user && user.role === 'partner') return <Navigate to="/partner" replace />;
  if (user) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

// Force ProjectWizard to remount when the project ID changes in the URL
function ProjectWizardKeyed() {
  const { id } = useParams<{ id: string }>();
  return <ProjectWizard key={id} />;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isAdmin, isSubAdmin, isStaff, isSupport, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner fullPage />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!user.emailConfirmed) {
    return <Navigate to="/confirm-email" replace />;
  }

  // Redirect admin/superadmin AND sub_admin to the admin console. Bug #3
  // (17.08) — sub_admin is a partial admin (operational sections); it must be
  // checked before the isStaff→/sales fallback below, since it is also isStaff.
  if (isAdmin || isSubAdmin) {
    return <Navigate to="/admin" replace />;
  }

  // Phase 2.2 — sales/va have no client_profiles row; a direct link into a
  // client-only route (e.g. "/dashboard") should land them on their own portal
  // instead of a dashboard built around a client project they don't have.
  if (isStaff) {
    return <Navigate to="/sales" replace />;
  }

  // #support-role — support staff land on the Tickets console, not the
  // client-project dashboard (they have no client_profiles row).
  if (isSupport) {
    return <Navigate to="/admin/tickets" replace />;
  }

  return <>{children}</>;
}

// Bug #3 (17.08) — `allowSubAdmin` opens a route to the sub_admin partial-admin
// tier IN ADDITION to admin/superadmin. Default is FALSE (fail-closed): a plain
// <AdminRoute allowSubAdmin> stays admin/superadmin-only, so the sensitive routes (/admin/users,
// /admin/settings) and any future admin route are restricted unless they opt in.
// The backend requireRole gates are the real enforcement; this is UX-level.
function AdminRoute({ children, allowSubAdmin = false, allowSupport = false }: { children: React.ReactNode; allowSubAdmin?: boolean; allowSupport?: boolean }) {
  const { user, isAdmin, isSubAdmin, isSupport, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner fullPage />;
  }

  // #support-role — `allowSupport` opens a route to the support tier IN ADDITION
  // to admin/superadmin (+ sub_admin if allowSubAdmin). Default FALSE, fail-closed.
  const ok = isAdmin || (allowSubAdmin && isSubAdmin) || (allowSupport && isSupport);
  if (!user || !ok) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function PartnerRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner fullPage />;
  }

  if (!user || user.role !== 'partner') {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

// Phase 2.2 — sales portal guard. Mirrors PartnerRoute's shape but allows the
// sales-staff roles PLUS admin/superadmin, matching sales.routes.ts's
// `requireRole('sales', 'sub_admin', 'admin', 'superadmin')` backend gate
// exactly (va is deliberately excluded — same as the backend). Cannot reuse
// AdminRoute here: AdminRoute only admits isAdmin (admin/superadmin), which
// would lock every sales/sub_admin rep out of their own portal.
function StaffRoute({ children }: { children: React.ReactNode }) {
  const { user, isStaff, isAdmin, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner fullPage />;
  }

  if (!user || !(isStaff || isAdmin)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

/**
 * Overseer (C813 product analytics) wiring. Waits for auth, then — through the pure gate — starts the SDK (lazy
 * chunks) for visitors, customers and partners only, identifies the signed-in user by id, and mutes staff,
 * impersonated and QA sessions. Route changes are captured by the SDK itself (it wraps history), so nothing here
 * listens to the router. All decisions live in services/overseerSync.ts so they can be tested without a DOM.
 */
function OverseerBridge() {
  const { user, loading, isImpersonating } = useAuth();
  useEffect(() => { syncOverseer({ loading, user, impersonating: isImpersonating }); }, [loading, user, isImpersonating]);
  return null;
}

export default function App() {
  // ImpersonationBanner is rendered OUTSIDE ErrorBoundary so an impersonating
  // admin can always click "Exit impersonation" even when a route crashes —
  // otherwise a render error replaces the entire tree (banner included) with
  // the fallback UI and the admin gets stuck inside the impersonation session.
  return (
    <>
      <OverseerBridge />
      <ImpersonationBanner />
      <ErrorBoundary>
      <main id="main-content" style={{ minHeight: '100vh' }}>
      <Suspense fallback={<LoadingSpinner fullPage />}>
      <Routes>
      {/* Home page: redirect logged-in users to dashboard, show checkout for guests */}
      <Route path="/" element={<HomeRoute><CheckoutPage /></HomeRoute>} />
      <Route path="/checkout" element={<CheckoutPage />} />

      {/* Branded checkout routes — kept for stable bookmarks; new partners
          flow through the dynamic /:brandSlug/checkout route below which
          resolves any brand_configs.slug → partner_owner_id at render time. */}
      <Route path="/creative813" element={<CheckoutPage brand="creative813" />} />
      <Route path="/creative813/checkout" element={<CheckoutPage brand="creative813" />} />
      <Route path="/gigapress" element={<CheckoutPage brand="gigapress" />} />
      <Route path="/gigapress/checkout" element={<CheckoutPage brand="gigapress" />} />
      {/* #TRYNOW (Phase 8 funnel) — free-only signup landing. Same
          gigapress-branded CheckoutPage; the `tryNow` prop hides the paid
          grid on Step 1 and auto-opens the existing free-trial modal,
          feeding the SAME register(...) free-signup path the /checkout
          banner's "Generate for Free" CTA already uses. */}
      <Route path="/try-now" element={<CheckoutPage brand="gigapress" tryNow />} />
      {/* Dynamic brand-slug checkout — works for every partner with a
          brand_configs.slug. Must come AFTER static routes so /login,
          /dashboard, /admin etc. still match first. */}
      <Route path="/:brandSlug/checkout" element={<CheckoutPage />} />

      {/* Auth */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<Navigate to="/checkout" replace />} />
      <Route path="/confirm-email" element={<EmailConfirmationPage />} />
      {/* Phase 4 (2026-05-18) — re-engagement share-link landing. Reads
          the token from the URL, exchanges it for a session JWT via
          /api/auth/resume/:token, then redirects to dashboard or
          /dashboard/project/:id/manage per the backend's `redirectTo`. */}
      <Route path="/resume/:token" element={<ResumePage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* User dashboard */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/project/:id"
        element={
          <ProtectedRoute>
            <ProjectWizardKeyed />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/project/:id/manage"
        element={
          <ProtectedRoute>
            <ManagePage />
          </ProtectedRoute>
        }
      />
      {/* Phase 1C — "Your website is live" celebration. Reached from a
          successful production deploy inside ProjectWizard and
          revisitable from the Dashboard for share links. */}
      <Route
        path="/project/:id/published"
        element={
          <ProtectedRoute>
            <PublishedPage />
          </ProtectedRoute>
        }
      />
      {/* Phase 2B — invite acceptance. Auto-redirects to login if needed. */}
      <Route path="/collab/accept" element={<CollabAcceptPage />} />

      {/* Outreach claim — auto-login + set password (public, no auth required) */}
      <Route path="/dashboard/project/:id/claim" element={<OutreachClaimPage />} />

      {/* Public outreach preview */}
      <Route path="/preview/:token" element={<PreviewPage />} />
      <Route path="/share/:token" element={<SharePage />} />
      {/* Phase 2.1 — public payment-link landing (checkout or card-update) */}
      <Route path="/pay/:token" element={<PaymentLinkLanding />} />
      {/* Public partner signup */}
      <Route path="/become-a-partner" element={<PartnerSignup />} />
      <Route path="/partner/register" element={<PartnerSignup />} />
      {/* Hidden agency landing — not linked from public nav; only accessible if you know the URL */}
      <Route path="/agency" element={<AgencySignupPage />} />
      {/* API docs (public — no login required to read) */}
      <Route path="/docs/api" element={<ApiDocsPage />} />
      {/* Public legal documents (content edited in admin settings) */}
      <Route path="/terms-of-service" element={<LegalContentPage slug="terms-of-service" />} />
      <Route path="/privacy-policy" element={<LegalContentPage slug="privacy-policy" />} />
      {/* API key management (customer, requires login) */}
      <Route
        path="/api-keys"
        element={
          <ProtectedRoute>
            <ApiKeysPage />
          </ProtectedRoute>
        }
      />
      {/* Customer support tickets */}
      <Route
        path="/support"
        element={
          <ProtectedRoute>
            <SupportPage />
          </ProtectedRoute>
        }
      />
      {/* #124 — client Account hub (profile, password, billing, plan) */}
      <Route
        path="/account"
        element={
          <ProtectedRoute>
            <AccountPage />
          </ProtectedRoute>
        }
      />
      {/* Phase 1.3 — customer self-service "Update card" page; the dunning
          email's "Update payment method" link lands here (with ?orderId=
          for context). Requires auth like the /billing/payment-method
          endpoint it calls. */}
      <Route
        path="/billing/update-card"
        element={
          <ProtectedRoute>
            <BillingPage />
          </ProtectedRoute>
        }
      />
      {/* Phase 1.4b — customer invoice history (list + auth'd PDF download). */}
      <Route
        path="/billing/history"
        element={
          <ProtectedRoute>
            <BillingHistoryPage />
          </ProtectedRoute>
        }
      />

      {/* Admin routes */}
      <Route
        path="/admin"
        element={
          <AdminRoute allowSubAdmin>
            <AdminDashboard />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/settings"
        element={
          <AdminRoute>
            <AdminSettingsPage />
          </AdminRoute>
        }
      />
      {/* #127 — superadmin capability matrix. AdminRoute admits admin+, but the
          page itself redirects non-superadmin and the backend is superadmin-only. */}
      <Route
        path="/admin/permissions"
        element={
          <AdminRoute>
            <AdminPermissions />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/clients"
        element={
          <AdminRoute allowSubAdmin allowSupport>
            <AdminClientsPage />
          </AdminRoute>
        }
      />
      {/* #17 (2026-08-12) — staff/user management, promoted from the
          "Manage staff" card that used to live on /sales. */}
      <Route
        path="/admin/users"
        element={
          <AdminRoute>
            <AdminUsersPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/packages"
        element={
          <AdminRoute allowSubAdmin>
            <AdminPackagesPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/orders"
        element={
          <AdminRoute allowSubAdmin allowSupport>
            <AdminOrdersPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/partners"
        element={
          <AdminRoute allowSubAdmin>
            <AdminPartnersPage />
          </AdminRoute>
        }
      />
      {/* #19 (2026-08-12) admin-nav consolidation — the 6 redirects below
          keep every old bookmark/link resolving after their pages were
          folded into a host page as a tab (?tab= selects the right one;
          AdminRoute still gates at the destination). */}
      <Route path="/admin/payouts" element={<Navigate to="/admin/partners?tab=payouts" replace />} />
      <Route path="/admin/promo-codes" element={<Navigate to="/admin/packages?tab=promo-codes" replace />} />
      <Route path="/admin/vps-analytics" element={<Navigate to="/admin/usage?tab=vps" replace />} />
      <Route path="/admin/bedrock-spend" element={<Navigate to="/admin/usage?tab=bedrock" replace />} />
      <Route path="/admin/email-campaigns" element={<Navigate to="/admin/settings?tab=email-campaigns" replace />} />
      <Route path="/admin/kb" element={<Navigate to="/admin/settings?tab=kb" replace />} />
      <Route
        path="/admin/outreach"
        element={
          <AdminRoute allowSubAdmin>
            <AdminOutreachPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/tickets"
        element={
          <AdminRoute allowSubAdmin allowSupport>
            <AdminTicketsPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/usage"
        element={
          <AdminRoute allowSubAdmin>
            <AdminUsageReportPage />
          </AdminRoute>
        }
      />
      {/* Phase 2.2 — sales portal (sales/sub_admin reps + admin/superadmin) */}
      <Route
        path="/sales"
        element={
          <StaffRoute>
            <SalesPortal />
          </StaffRoute>
        }
      />

      {/* Partner routes */}
      <Route path="/partner" element={<PartnerRoute><PartnerDashboard /></PartnerRoute>} />
      <Route path="/partner/outreach" element={<PartnerRoute><PartnerOutreach /></PartnerRoute>} />
      <Route path="/partner/clients" element={<PartnerRoute><PartnerClients /></PartnerRoute>} />
      <Route path="/partner/settings" element={<PartnerRoute><PartnerSettings /></PartnerRoute>} />
      {/* Phase 2: dedicated affiliate console — slimmer than the reseller
          dashboard. Affiliates land here directly after login. */}
      <Route path="/partner/affiliate" element={<PartnerRoute><AffiliateDashboard /></PartnerRoute>} />

      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
      </main>
      </ErrorBoundary>
    </>
  );
}
